import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';

type OutageType = 'Planned' | 'Forced';
type OutageStatus = 'Active' | 'Scheduled' | 'Completed';

type Option = { code: string; name: string };
type UnitOption = Option & {
  generatingCompanyCode: string;
  generatingCompanyName: string;
  qcaCode: string;
  pssCode: string;
  pssName: string;
  installedCapacityMw: number;
};

type Outage = {
  id: number;
  outageId: string;
  qcaCode: string;
  pssCode: string;
  generatingCompanyCode: string;
  generatingUnitCode: string;
  outageType: OutageType;
  startDateTime: string;
  endDateTime: string;
  status: OutageStatus;
  reason: string;
  description: string;
  expectedImpactMw: number;
  duration?: string;
  qcaName?: string;
  pssName?: string;
  generatingCompanyName?: string;
  generatingUnitName?: string;
  documentName?: string;
};

type OutageRow = Outage & {
  qcaName: string;
  pssName: string;
  generatingCompanyName: string;
  generatingUnitName: string;
  installedCapacityMw: number;
};

type OutageMock = {
  header: { title: string; description: string };
  outages: Outage[];
};

type ReferenceData = {
  qcas: Option[];
  psses: (Option & { qcaCode: string })[];
  companies: Option[];
  units: UnitOption[];
};

const DEFAULT_DATA: OutageMock = {
  header: {
    title: 'Outage Management',
    description: 'Plan, monitor, and review generating unit outages across the registered portfolio.'
  },
  outages: []
};

const EMPTY_REFERENCES: ReferenceData = { qcas: [], psses: [], companies: [], units: [] };

@Component({
  selector: 'app-outage-management',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './outage-management.component.html',
  styleUrls: ['./outage-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutageManagementComponent {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  readonly pageData = signal<OutageMock>(DEFAULT_DATA);
  readonly references = signal<ReferenceData>(EMPTY_REFERENCES);
  readonly outages = signal<Outage[]>([]);
  readonly searchTerm = signal('');
  readonly selectedQca = signal('All');
  readonly selectedPss = signal('All');
  readonly selectedCompany = signal('All');
  readonly selectedUnit = signal('All');
  readonly selectedStatus = signal('All');
  readonly selectedType = signal('All');
  readonly fromDate = signal('');
  readonly toDate = signal('');
  readonly currentPage = signal(1);
  readonly pageSize = signal(10);
  readonly isFormOpen = signal(false);
  readonly editingOutageId = signal<number | null>(null);
  readonly formError = signal<string | null>(null);
  readonly toast = signal<{ message: string; type: 'success' | 'error' } | null>(null);
  readonly formQcaCode = signal('');
  readonly formPssCode = signal('');
  readonly formCompanyCode = signal('');

  readonly title = computed(() => this.route.snapshot.data['title'] as string || this.pageData().header.title);
  readonly description = computed(() => this.route.snapshot.data['description'] as string || this.pageData().header.description);
  readonly qcas = computed(() => this.references().qcas);
  readonly psses = computed(() => this.references().psses);
  readonly filteredPsses = computed(() => {
    const qca = this.formQcaCode();
    return this.psses().filter((item) => !qca || item.qcaCode === qca);
  });
  readonly companies = computed(() => this.references().companies);
  readonly units = computed(() => this.references().units);
  readonly pssOptions = computed(() => ['All', ...this.psses().map((item) => item.code)]);
  readonly companyOptions = computed(() => ['All', ...this.companies().map((item) => item.code)]);
  readonly unitOptions = computed(() => ['All', ...this.units().map((item) => item.code)]);
  readonly statusOptions = ['All', 'Active', 'Scheduled', 'Completed'];
  readonly typeOptions = ['All', 'Planned', 'Forced'];

  readonly form = this.fb.nonNullable.group({
    qcaCode: ['', Validators.required],
    pssCode: ['', Validators.required],
    generatingCompanyCode: ['', Validators.required],
    generatingUnitCode: ['', Validators.required],
    outageType: ['Planned' as OutageType, Validators.required],
    startDateTime: ['', Validators.required],
    endDateTime: ['', Validators.required],
    reason: ['', Validators.required],
    description: [''],
    expectedImpactMw: [null as number | null, [Validators.required, Validators.min(0.01)]],
    documentName: ['']
  });

  readonly filteredUnits = computed(() => {
    const qca = this.formQcaCode();
    const pss = this.formPssCode();
    const company = this.formCompanyCode();
    return this.units().filter((unit) =>
      (!qca || unit.qcaCode === qca) &&
      (!pss || unit.pssCode === pss) &&
      (!company || unit.generatingCompanyCode === company)
    );
  });

  readonly rows = computed<OutageRow[]>(() => {
    const qcaMap = new Map(this.qcas().map((item) => [item.code, item.name]));
    const pssMap = new Map(this.psses().map((item) => [item.code, item.name]));
    const companyMap = new Map(this.companies().map((item) => [item.code, item.name]));
    const unitMap = new Map(this.units().map((item) => [item.code, item]));
    return this.outages().map((outage) => {
      const unit = unitMap.get(outage.generatingUnitCode);
      return {
        ...outage,
        qcaName: qcaMap.get(outage.qcaCode) || outage.qcaName || outage.qcaCode,
        pssName: pssMap.get(outage.pssCode) || outage.pssName || outage.pssCode,
        generatingCompanyName: companyMap.get(outage.generatingCompanyCode) || outage.generatingCompanyName || outage.generatingCompanyCode,
        generatingUnitName: unit?.name || outage.generatingUnitName || outage.generatingUnitCode,
        installedCapacityMw: unit?.installedCapacityMw || outage.expectedImpactMw
      };
    });
  });

  readonly filteredRows = computed(() => {
    const search = this.searchTerm().trim().toLowerCase();
    const qca = this.selectedQca();
    const pss = this.selectedPss();
    const company = this.selectedCompany();
    const unit = this.selectedUnit();
    const status = this.selectedStatus();
    const type = this.selectedType();
    const from = this.fromDate();
    const to = this.toDate();
    return this.rows().filter((row) => {
      const searchable = [row.outageId, row.qcaName, row.pssName, row.generatingCompanyName, row.generatingUnitName, row.reason].join(' ').toLowerCase();
      return (!search || searchable.includes(search)) &&
        (qca === 'All' || row.qcaCode === qca) &&
        (pss === 'All' || row.pssCode === pss) &&
        (company === 'All' || row.generatingCompanyCode === company) &&
        (unit === 'All' || row.generatingUnitCode === unit) &&
        (status === 'All' || row.status === status) &&
        (type === 'All' || row.outageType === type) &&
        (!from || row.startDateTime.slice(0, 10) >= from) &&
        (!to || row.endDateTime.slice(0, 10) <= to);
    });
  });

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredRows().length / this.pageSize())));
  readonly paginatedRows = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.filteredRows().slice(start, start + this.pageSize());
  });
  readonly pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));
  readonly showingText = computed(() => {
    const total = this.filteredRows().length;
    if (!total) return 'Showing 0 entries';
    const start = ((this.currentPage() - 1) * this.pageSize()) + 1;
    const end = Math.min(this.currentPage() * this.pageSize(), total);
    return `Showing ${start} to ${end} of ${total} entries`;
  });
  readonly stats = computed(() => {
    const outages = this.outages();
    return [
      { label: 'Total Units', value: this.units().length.toLocaleString('en-IN'), icon: '▥', tone: 'blue' },
      { label: 'Units in Outage', value: new Set(outages.map((row) => row.generatingUnitCode)).size.toLocaleString('en-IN'), icon: '♧', tone: 'amber' },
      { label: 'Planned Outage', value: outages.filter((row) => row.outageType === 'Planned').length.toLocaleString('en-IN'), icon: '▣', tone: 'violet' },
      { label: 'Forced Outage', value: outages.filter((row) => row.outageType === 'Forced').length.toLocaleString('en-IN'), icon: 'ϟ', tone: 'red' },
      { label: 'Total Outage Capacity', value: outages.reduce((sum, row) => sum + row.expectedImpactMw, 0).toLocaleString('en-IN', { maximumFractionDigits: 2 }), suffix: 'MW', icon: '◉', tone: 'blue' }
    ];
  });

  constructor() {
    forkJoin({
      outage: this.http.get<OutageMock>('json/outage-management.mock.json'),
      qca: this.http.get<{ rows: { code: string; name: string }[] }>('json/qca-management.mock.json'),
      pss: this.http.get<{ rows: { code: string; name: string; qcaCode: string }[] }>('json/pss-management.mock.json'),
      companies: this.http.get<{ rows: { code: string; name: string }[] }>('json/generating-company-management.mock.json'),
      generators: this.http.get<{ unitCatalog: { code: string; uniqueNo: string; generator: string; installedCapacityMw: number }[]; rows: { companyCode: string; companyName: string; qcaCode: string; pssCode: string; pssName: string }[] }>('json/generators-management.mock.json')
    }).subscribe({
      next: ({ outage, qca, pss, companies, generators }) => {
        const companyOptions = companies.rows.map((item) => ({ code: item.code, name: item.name }));
        const companyByName = new Map(companyOptions.map((item) => [item.name, item]));
        const generatorRows = generators.rows;
        const pssRows = pss.rows;
        const units = generators.unitCatalog.map((unit) => {
          const company = companyByName.get(unit.generator) || { code: '', name: unit.generator };
          const generator = generatorRows.find((item) => item.companyCode === company.code || item.companyName === unit.generator);
          const pssItem = pssRows.find((item) => item.code === generator?.pssCode);
          return {
            code: unit.code,
            name: unit.uniqueNo,
            generatingCompanyCode: company.code,
            generatingCompanyName: company.name,
            qcaCode: generator?.qcaCode || pssItem?.qcaCode || 'QCA-001',
            pssCode: generator?.pssCode || pssItem?.code || 'PSS-001',
            pssName: generator?.pssName || pssItem?.name || 'PSS-001',
            installedCapacityMw: unit.installedCapacityMw
          };
        });
        this.pageData.set(outage);
        this.outages.set(outage.outages);
        this.references.set({
          qcas: qca.rows.map((item) => ({ code: item.code, name: item.name })),
          psses: pssRows.map((item) => ({ code: item.code, name: item.name, qcaCode: item.qcaCode })),
          companies: companyOptions,
          units
        });
      },
      error: () => this.toast.set({ message: 'Unable to load outage reference data.', type: 'error' })
    });

    this.form.controls.qcaCode.valueChanges.subscribe((value) => {
      this.formQcaCode.set(value);
      const pss = this.form.controls.pssCode.value;
      if (pss && this.psses().find((item) => item.code === pss)?.qcaCode !== value) {
        this.form.patchValue({ pssCode: '', generatingUnitCode: '' }, { emitEvent: false });
      }
    });
    this.form.controls.pssCode.valueChanges.subscribe((value) => this.formPssCode.set(value));
    this.form.controls.generatingCompanyCode.valueChanges.subscribe(() => {
      this.formCompanyCode.set(this.form.controls.generatingCompanyCode.value);
      const unit = this.form.controls.generatingUnitCode.value;
      if (unit && !this.filteredUnits().some((item) => item.code === unit)) {
        this.form.patchValue({ generatingUnitCode: '' }, { emitEvent: false });
      }
    });
  }

  openCreateForm(): void {
    this.editingOutageId.set(null);
    this.form.reset({ qcaCode: '', pssCode: '', generatingCompanyCode: '', generatingUnitCode: '', outageType: 'Planned', startDateTime: '', endDateTime: '', reason: '', description: '', expectedImpactMw: null, documentName: '' });
    this.formQcaCode.set(''); this.formPssCode.set(''); this.formCompanyCode.set('');
    this.formError.set(null);
    this.isFormOpen.set(true);
  }

  openEditForm(row: OutageRow): void {
    this.editingOutageId.set(row.id);
    this.form.reset({
      qcaCode: row.qcaCode,
      pssCode: row.pssCode,
      generatingCompanyCode: row.generatingCompanyCode,
      generatingUnitCode: row.generatingUnitCode,
      outageType: row.outageType,
      startDateTime: row.startDateTime,
      endDateTime: row.endDateTime,
      reason: row.reason,
      description: row.description,
      expectedImpactMw: row.expectedImpactMw,
      documentName: row.documentName || ''
    });
    this.formQcaCode.set(row.qcaCode);
    this.formPssCode.set(row.pssCode);
    this.formCompanyCode.set(row.generatingCompanyCode);
    this.formError.set(null);
    this.isFormOpen.set(true);
  }

  closeForm(): void { this.isFormOpen.set(false); this.editingOutageId.set(null); this.formError.set(null); }

  goToPssDashboard(pssCode: string): void {
    void this.router.navigate(['/pss-dashboard', pssCode]);
  }

  goToQcaDashboard(qcaCode: string): void {
    void this.router.navigate(['/qca-dashboard'], { queryParams: { qcaCode } });
  }

  save(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); this.formError.set('Please fill all required fields.'); return; }
    const value = this.form.getRawValue();
    if (value.startDateTime > value.endDateTime) { this.formError.set('Start date and time must be before the end date and time.'); return; }
    const unit = this.units().find((item) => item.code === value.generatingUnitCode);
    if (!unit) { this.formError.set('Select a valid generating unit.'); return; }
    const editingId = this.editingOutageId();
    const id = editingId ?? this.outages().reduce((max, row) => Math.max(max, row.id), 0) + 1;
    const qca = this.qcas().find((item) => item.code === value.qcaCode);
    const pss = this.psses().find((item) => item.code === value.pssCode);
    const company = this.companies().find((item) => item.code === value.generatingCompanyCode);
    const next: Outage = {
      id, outageId: `OUT-2026-${String(id).padStart(4, '0')}`,
      qcaName: qca?.name,
      pssName: pss?.name,
      generatingCompanyName: company?.name,
      generatingUnitName: unit.name,
      qcaCode: value.qcaCode, pssCode: value.pssCode, generatingCompanyCode: value.generatingCompanyCode,
      generatingUnitCode: value.generatingUnitCode, outageType: value.outageType, startDateTime: value.startDateTime,
      endDateTime: value.endDateTime, status: 'Scheduled', reason: value.reason, description: value.description,
      expectedImpactMw: Number(value.expectedImpactMw), documentName: value.documentName || undefined
    };
    this.outages.update((rows) => editingId === null ? [next, ...rows] : rows.map((row) => row.id === editingId ? { ...row, ...next, outageId: row.outageId, status: row.status } : row));
    this.currentPage.set(1);
    this.closeForm();
    this.toast.set({ message: editingId === null ? 'Outage added successfully.' : 'Outage updated successfully.', type: 'success' });
  }

  deleteOutage(row: OutageRow): void {
    if (!confirm(`Delete outage "${row.outageId}"?`)) return;
    this.outages.update((rows) => rows.filter((item) => item.id !== row.id));
    this.currentPage.set(Math.min(this.currentPage(), this.totalPages()));
    this.toast.set({ message: 'Outage deleted successfully.', type: 'success' });
  }

  resetFilters(): void {
    this.searchTerm.set(''); this.selectedQca.set('All'); this.selectedPss.set('All'); this.selectedCompany.set('All'); this.selectedUnit.set('All'); this.selectedStatus.set('All'); this.selectedType.set('All'); this.fromDate.set(''); this.toDate.set(''); this.currentPage.set(1);
  }
  updateSearch(value: string): void { this.searchTerm.set(value); this.currentPage.set(1); }
  onDocumentSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    this.form.controls.documentName.setValue(file?.name || '');
  }
  goToPage(page: number): void { this.currentPage.set(Math.min(Math.max(page, 1), this.totalPages())); }
  formatDate(value: string): string { const date = new Date(value); return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(date); }
  formatDateTime(value: string): string { const date = new Date(value); return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date); }
}
